import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Brain, Shield, Search, Code, Zap, Server, GraduationCap, Target, Cloud, BarChart,
  TrendingUp, Palette, Share2, FileText, PenTool, Megaphone, Video, Globe, LineChart, Lightbulb,
  MapPin, Mail, Phone, ArrowRight, Sparkles, ChevronRight,
  Building2, Eye, Rocket, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { startups, applicationFormUrl } from '../data/mock';

const iconMap = {
  Brain, Shield, Search, Code, Zap, Server, GraduationCap, Target, Cloud, BarChart,
  TrendingUp, Palette, Share2, FileText, PenTool, Megaphone, Video, Globe, LineChart, Lightbulb
};

const StartupPage = () => {
  const { id } = useParams();
  const startup = startups.find(s => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!startup) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Startup Not Found</h1>
            <Link to="/startups">
              <Button>View All Startups</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const isBitech = startup.id === 'bitech-ai';

  return (
    <Layout>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px ${startup.colors.primary}40; }
          50% { box-shadow: 0 0 60px ${startup.colors.primary}60; }
        }
        @keyframes grid-move {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        @keyframes scan-line {
          0% { top: -100%; }
          100% { top: 100%; }
        }
        @keyframes data-flow {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 100%; }
        }
        .startup-hero-bitech {
          background: linear-gradient(135deg, #000a14 0%, #001a33 30%, #003366 70%, #004080 100%);
        }
        .startup-hero-afrovanta {
          background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 30%, #16213e 70%, #0f3460 100%);
        }
        .cyber-grid {
          background-image: 
            linear-gradient(rgba(0, 102, 204, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 204, 0.1) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: grid-move 20s linear infinite;
        }
        .scan-effect::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, ${startup.colors.accent}, transparent);
          animation: scan-line 4s ease-in-out infinite;
        }
        .service-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          background: linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%);
        }
        .service-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 25px 50px ${startup.colors.primary}30;
        }
        .service-card:hover .service-icon {
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 10px 30px ${startup.colors.primary}40;
        }
        .highlight-card {
          background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.1);
        }
        .highlight-card:hover {
          border-color: ${startup.colors.accent}50;
          box-shadow: 0 0 30px ${startup.colors.accent}20;
        }
        .logo-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        .text-gradient {
          background: linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.accent} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .btn-glow:hover {
          box-shadow: 0 0 30px ${startup.colors.primary}60;
        }
        .floating-element {
          animation: float 6s ease-in-out infinite;
        }
        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: ${startup.colors.accent};
          border-radius: 50%;
          opacity: 0.6;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[90vh] flex items-center overflow-hidden ${isBitech ? 'startup-hero-bitech' : 'startup-hero-afrovanta'}`}>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Cyber Grid */}
          <div className="absolute inset-0 cyber-grid opacity-30" />
          
          {/* Glowing Orbs */}
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 blur-3xl"
               style={{ background: `radial-gradient(circle, ${startup.colors.accent} 0%, transparent 70%)` }} />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl"
               style={{ background: `radial-gradient(circle, ${startup.colors.primary} 0%, transparent 70%)` }} />
          <div className="absolute top-1/2 left-1/3 w-72 h-72 rounded-full opacity-10 blur-2xl floating-element"
               style={{ background: startup.colors.secondary }} />
          
          {/* Scan Effect */}
          <div className="absolute inset-0 scan-effect overflow-hidden" />
        </div>

        <div className="container-custom relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
                   style={{ background: `${startup.colors.primary}30`, border: `1px solid ${startup.colors.primary}50` }}>
                <Sparkles className="w-4 h-4" style={{ color: startup.colors.accent }} />
                <span className="text-sm font-medium" style={{ color: startup.colors.accent }}>BIT Innovation Ecosystem</span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                {startup.name}
              </h1>

              {/* Tagline */}
              <p className="text-xl md:text-2xl mb-6 font-medium" style={{ color: startup.colors.accent }}>
                {startup.tagline}
              </p>

              {/* Description */}
              <p className="text-lg text-gray-300 mb-4 leading-relaxed text-justify">
                {startup.description}
              </p>
              {startup.fullDescription && (
                <p className="text-base text-gray-400 mb-8 leading-relaxed text-justify">
                  {startup.fullDescription}
                </p>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-10">
                <a href={`mailto:${startup.email}`}>
                  <Button size="lg" className="btn-glow rounded-full px-8 text-white transition-all duration-300"
                          style={{ background: `linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.secondary} 100%)` }}>
                    Get in Touch
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10 transition-all duration-300">
                    Contact BIT
                  </Button>
                </Link>
              </div>

              {/* Quick Info */}
              <div className="flex flex-wrap gap-6 text-gray-400">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" style={{ color: startup.colors.accent }} />
                  <span>{startup.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" style={{ color: startup.colors.accent }} />
                  <span>Est. {startup.founded}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" style={{ color: startup.colors.accent }} />
                  <span>{startup.email}</span>
                </div>
              </div>
            </div>

            {/* Logo Section */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow Background */}
                <div className="absolute inset-0 rounded-3xl blur-3xl opacity-30"
                     style={{ background: `linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.accent} 100%)` }} />
                
                {/* Logo Container */}
                <div className="relative logo-glow rounded-3xl p-8 bg-black/50 backdrop-blur-xl border border-white/10">
                  <img 
                    src={startup.logo} 
                    alt={`${startup.name} Logo`}
                    className="w-64 h-64 md:w-80 md:h-80 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 text-white" style={{ background: startup.colors.primary }}>
              Our Services
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What We <span className="text-gradient">Offer</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Comprehensive solutions designed to empower your business and drive digital transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {startup.services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Zap;
              return (
                <div key={index} 
                     className="service-card rounded-2xl p-6 shadow-lg border border-gray-100 cursor-pointer group">
                  <div className="service-icon w-16 h-16 rounded-2xl mb-5 flex items-center justify-center transition-all duration-300"
                       style={{ background: `linear-gradient(135deg, ${startup.colors.primary}15 0%, ${startup.colors.accent}15 100%)` }}>
                    <IconComponent className="w-8 h-8" style={{ color: startup.colors.primary }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-gradient transition-all duration-300">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed text-justify">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="relative group">
              <div className="absolute -inset-1 rounded-3xl opacity-20 blur-xl transition-all duration-500 group-hover:opacity-40"
                   style={{ background: `linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.accent} 100%)` }} />
              <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-xl h-full">
                <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center"
                     style={{ background: `linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.secondary} 100%)` }}>
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed text-lg text-justify">{startup.vision}</p>
              </div>
            </div>

            {/* Mission */}
            <div className="relative group">
              <div className="absolute -inset-1 rounded-3xl opacity-20 blur-xl transition-all duration-500 group-hover:opacity-40"
                   style={{ background: `linear-gradient(135deg, ${startup.colors.secondary} 0%, ${startup.colors.accent} 100%)` }} />
              <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-xl h-full">
                <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center"
                     style={{ background: `linear-gradient(135deg, ${startup.colors.secondary} 0%, ${startup.colors.accent} 100%)` }}>
                  <Rocket className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed text-lg text-justify">{startup.mission}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation Highlights */}
      <section className="py-24" style={{ background: startup.colors.gradient }}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/10 text-white border-white/20">
              <Rocket className="w-4 h-4 mr-2" />
              Innovation Pipeline
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Coming Soon
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg">
              Exciting innovations currently in development. Stay tuned for groundbreaking solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {startup.highlights.map((highlight, index) => (
              <div key={index} className="highlight-card rounded-2xl p-8 transition-all duration-300 cursor-pointer">
                <Badge className="mb-4" style={{ background: `${startup.colors.accent}30`, color: startup.colors.accent }}>
                  {highlight.status}
                </Badge>
                <h3 className="text-xl font-bold text-white mb-4">{highlight.title}</h3>
                <p className="text-gray-400 leading-relaxed text-justify">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Info Side */}
                <div className="p-12" style={{ background: startup.colors.gradient }}>
                  <h3 className="text-3xl font-bold text-white mb-6">Let's Work Together</h3>
                  <p className="text-white/80 mb-10 text-lg leading-relaxed text-justify">
                    Ready to transform your business with cutting-edge solutions? Get in touch and let's discuss how we can help you achieve your goals.
                  </p>
                  
                  <div className="space-y-6">
                    <a href={`mailto:${startup.email}`} className="flex items-center gap-4 text-white/90 hover:text-white transition-colors group">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Email Us</p>
                        <span className="font-medium">{startup.email}</span>
                      </div>
                    </a>
                    <a href={`tel:${startup.phone}`} className="flex items-center gap-4 text-white/90 hover:text-white transition-colors group">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Call Us</p>
                        <span className="font-medium">{startup.phone}</span>
                      </div>
                    </a>
                    <div className="flex items-center gap-4 text-white/90">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Globe className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Website</p>
                        <span className="font-medium">{startup.website}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-white/90">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Location</p>
                        <span className="font-medium">{startup.location}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Side */}
                <div className="p-12 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Start Your Project Today</h4>
                  <p className="text-gray-600 mb-8 text-lg text-justify">
                    Whether you need {isBitech ? 'AI solutions, cybersecurity, or digital forensics' : 'digital marketing, branding, or creative media'}, we're here to help your business succeed.
                  </p>
                  <div className="space-y-4">
                    <a href={`mailto:${startup.email}?subject=Project Inquiry - ${startup.name}`} className="block">
                      <Button className="w-full rounded-full py-6 text-lg text-white btn-glow" style={{ background: startup.colors.primary }}>
                        Send Us a Message
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </a>
                    <Link to="/startups" className="block">
                      <Button variant="outline" className="w-full rounded-full py-6 text-lg">
                        View All Startups
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back Navigation */}
      <section className="py-8 bg-white border-t">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to BIT Home
            </Link>
            <p className="text-sm text-gray-500">
              Part of <span className="font-semibold" style={{ color: startup.colors.primary }}>BIT Innovation Ecosystem</span>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default StartupPage;
